import type { WorkoutSet } from "./types";

export type TimedWorkPhase = "work" | "rest";

export type TimedWorkReadyState = {
  activeSetId: string | null;
  pendingSetId: null;
  phase: "work";
  seconds: number;
  preset: number;
  timerActive: false;
};

export type WorkCompletionTransition =
  | { kind: "rest"; completedSetId: string; pendingSetId: string }
  | { kind: "finished"; completedSetId: string }
  | { kind: "noop" };

export type RestCompletionTransition =
  | { kind: "work"; nextSet: WorkoutSet }
  | { kind: "finished" };

/**
 * Prepares a timed exercise without starting it. The athlete remains in
 * control of the first start action.
 */
export function createInitialTimedWorkState(
  sets: WorkoutSet[],
  defaultDuration: number
): TimedWorkReadyState {
  const activeSet = sets.find((set) => !set.completed) ?? null;
  const duration = activeSet?.durationSeconds ?? defaultDuration;

  return {
    activeSetId: activeSet?.id ?? null,
    pendingSetId: null,
    phase: "work",
    seconds: activeSet ? duration : 0,
    preset: duration,
    timerActive: false,
  };
}

/**
 * Returns the next incomplete set after the active set in the established
 * exercise/set order. Timed-work progression deliberately advances only at
 * the rest-to-work boundary, never at work completion.
 */
export function findNextIncompleteTimedSet(
  sets: WorkoutSet[],
  activeSetId: string
): WorkoutSet | null {
  const activeIndex = sets.findIndex((set) => set.id === activeSetId);

  if (activeIndex === -1) return null;

  return sets.slice(activeIndex + 1).find((set) => !set.completed) ?? null;
}

/**
 * Resolves the successor stored when work completed. If the stored successor
 * was manually completed during rest, fall back to the next remaining
 * incomplete set so the timer never starts a completed or nonexistent set.
 */
export function resolvePendingTimedSet(
  sets: WorkoutSet[],
  pendingSetId: string | null
): WorkoutSet | null {
  const pendingSet = pendingSetId
    ? sets.find((set) => set.id === pendingSetId && !set.completed)
    : null;

  return pendingSet ?? sets.find((set) => !set.completed) ?? null;
}

/**
 * Determines the work-completion boundary. It does not mutate the set or
 * advance the active set; the successor remains pending until rest ends.
 */
export function getWorkCompletionTransition(
  sets: WorkoutSet[],
  activeSetId: string | null
): WorkCompletionTransition {
  const activeSet = activeSetId
    ? sets.find((set) => set.id === activeSetId)
    : null;

  if (!activeSet || activeSet.completed) return { kind: "noop" };

  const successor = findNextIncompleteTimedSet(sets, activeSet.id);
  return successor
    ? { kind: "rest", completedSetId: activeSet.id, pendingSetId: successor.id }
    : { kind: "finished", completedSetId: activeSet.id };
}

/**
 * Resolves the rest-to-work boundary. The pending successor is preferred,
 * while a later manual completion safely falls back to another unfinished set.
 */
export function getRestCompletionTransition(
  sets: WorkoutSet[],
  pendingSetId: string | null
): RestCompletionTransition {
  const successor = resolvePendingTimedSet(sets, pendingSetId);
  return successor ? { kind: "work", nextSet: successor } : { kind: "finished" };
}
